import { computed, Injectable, signal } from '@angular/core';
import { DataSource } from '../../table';
import { ApiFilterRequest } from '../../table/api/request/api-filter-request';
import { ListApiFunction } from '../../table/datasource/data-source';

/**
 * DataSourceManager gestisce il DataSource con Signals
 * Fornisce state e operazioni per il DataSource
 */
@Injectable({ providedIn: 'root' })
export class DataSourceManager<T> {
  private readonly _dataSource = signal<DataSource<T> | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly dataSource$ = computed(() => this._dataSource());
  readonly isLoading$ = computed(() => this._isLoading());
  readonly hasError$ = computed(() => this._error() !== null);
  readonly errorMessage$ = computed(() => this._error() || '');
  readonly rowsCount$ = computed(() => {
    const ds = this._dataSource();
    return ds?.rows?.length ?? 0;
  });

  initialize(
    apiFunction: ListApiFunction<T>,
    initialFilters: ApiFilterRequest[] = [],
    initialSort?: { dir: 'asc' | 'desc'; prop: string }
  ): void {
    (this._isLoading as any).set(true);
    (this._error as any).set(null);

    const datasource = new DataSource<T>({ observableFn: apiFunction, filters: initialFilters });
    // apply initial sort if provided (DataSource uses tableSort internally)
    if (initialSort) {
      // DataSource doesn't expose a public sort setter; manipulate protected property via casting
      (datasource as any).tableSort = initialSort ? [initialSort] : [];
    }
    // expose initial datasource instance immediately so consumers can bind to it
    (this._dataSource as any).set(datasource);
    // Note: do not call start() here - let TableComponent call it during ngOnInit
    // to ensure rows$ Observable has subscribers before fetch$ is emitted
  }

  applyFilters(filters: ApiFilterRequest[]): void {
    const ds = this._dataSource();
    if (ds) {
      (ds as any).filtering(filters);
      const cloneDataSource: DataSource<T> = Object.assign(
        Object.create(Object.getPrototypeOf(ds)),
        ds
      ) as DataSource<T>;
      (this._dataSource as any).set(cloneDataSource);
    }
  }

  refresh(): void {
    const ds = this._dataSource();
    if (ds) {
      (this._isLoading as any).set(true);
      (ds as any).refresh();
      const cloneDataSource: DataSource<T> = Object.assign(
        Object.create(Object.getPrototypeOf(ds)),
        ds
      ) as DataSource<T>;
      (this._dataSource as any).set(cloneDataSource);
    }
  }

  reset(): void {
    this._dataSource.set(null);
    (this._isLoading as any).set(false);
    (this._error as any).set(null);
  }

  getDataSource(): DataSource<T> | null {
    return this._dataSource();
  }
}
